import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getIcon } from './Icons';

const timelineStyles = `
  .vertical-timeline-tracker {
    font-family: "BentonSans", Helvetica, Arial, sans-serif;
  }
  .vertical-timeline-tracker .vt-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .vertical-timeline-tracker .vt-item {
    display: flex;
    align-items: stretch;
    position: relative;
  }
  .vertical-timeline-tracker .vt-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 24px;
    min-width: 24px;
    position: relative;
    top:3.5px;
  }
  .vertical-timeline-tracker .vt-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #c8c9c7;
    background: #fff;
    z-index: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }
  .vertical-timeline-tracker .vt-dot--parent {
    width: 15px;
    height: 15px;
    border-color: #006fcf;
    background: #006fcf;
  }
  .vertical-timeline-tracker .vt-dot--sub {
    width: 14px;
    height: 14px;
    border: 2.5px solid #006fcf;
  }
  .vertical-timeline-tracker .vt-dot--active {
    border-color: #00175a;
    background: #00175a;
  }
  .vertical-timeline-tracker .vt-dot--sub.vt-dot--active {
    border-color: #006fcf;
    background: #006fcf;
  }
  .vertical-timeline-tracker .vt-dot--current {
    border-color: #006fcf;
    background: #006fcf;
  }
  .vertical-timeline-tracker .vt-dot--disabled {
    border-color: #006fcf;
    background: #006fcf;
    cursor: not-allowed;
  }
  .vertical-timeline-tracker .vt-dot--parent.vt-dot--disabled {
    border-color: #006fcf;
    background: #006fcf;
    outline-color: #006fcf;
    cursor: not-allowed;
  }
  .vertical-timeline-tracker .vt-dot--parent.vt-dot--active {
    border-color: #006fcf;
    background: #006fcf;
    outline-color: #006fcf;
    width: 17px;
    height: 17px;
  }
  .vertical-timeline-tracker .vt-dot--parent.vt-dot--current {
    border-color: #00175a;
    background: #00175a;
    outline-color: #00175a;
    outline: 2px solid #00175a;
    outline-offset: 2px;
  }
  .vertical-timeline-tracker .vt-dot svg {
    width: 12px;
    height: 12px;
    fill: #fff;
  }
  .vertical-timeline-tracker .vt-dot--sub svg {
    width: 8px;
    height: 8px;
  }
  .vertical-timeline-tracker .vt-line {
    width: 0;
    flex-grow: 1;
    min-height: 8px;
    border-left: 2px dotted #808080;
    transition: border-color 0.2s ease;
  }
  .vertical-timeline-tracker .vt-line--active {
    border-color: #808080;
    border-style: solid;
    border-width: 1px;
  }
  .vertical-timeline-tracker .vt-content {
    padding: 0 0 0 12px;
    flex: 1;
    display: flex;
    align-items: start;
    min-height: 28px;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-content {
    min-height: 32px;
  }
  .vertical-timeline-tracker .vt-item--sub .vt-content {
    min-height: 24px;
    padding-left: 24px;
  }
  .vertical-timeline-tracker .vt-item--sub .vt-connector {
    justify-content: center;
  }
  .vertical-timeline-tracker .vt-label {
    font-size: 14px;
    font-weight: 700;
    color: #006fcf;
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    text-align: left;
    line-height: 23px;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }
  .vertical-timeline-tracker .vt-label--sub {
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-label--clickable {
    cursor: pointer;
  }
  .vertical-timeline-tracker .vt-label--clickable:hover {
    color: #1F6497;
  }
  .vertical-timeline-tracker .vt-label--disabled {
    color: #006fcf;
    cursor: not-allowed;
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-label--current {
    color: #006fcf;
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-label--active {
    color: #006fcf;
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-item--sub .vt-label--current {
    color: #01165E;
    font-weight: 600;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-label {
    color: #006fcf;
    cursor: pointer;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-label--disabled {
    color: #006fcf;
    cursor: not-allowed;
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-label--active {
    color: #006fcf;
    font-weight: 500;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-label--current {
    color: #01165E;
    font-weight: 600;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-line {
    border-color: #808080;
  }
  .vertical-timeline-tracker .vt-item--parent .vt-line--active {
    border-color: #808080;
    border-width: 1px;
    border-style: solid;
  }
  .vertical-timeline-tracker .vt-icon {
    flex-shrink: 0;
  }

  .vertical-timeline-tracker .vt-lineHeight--parent {
    min-height: 22px;
  }

  .vertical-timeline-tracker .vt-lineHeight--sub {
    min-height: 36px;
  }
`;

// Checkmark icon rendered inside completed parent dots
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: '10px', height: '10px', fill: '#fff' }}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// Pure: derives child step state from current position and max visited
const getStepState = (stepValue, currentNavValue, maxVisited) => {
  const v = Number(stepValue).toFixed(1);
  const curr = Number(currentNavValue).toFixed(1);
  if (v === curr) return 'current';
  if (v <= Number(maxVisited).toFixed(1)) return 'active';
  return 'disabled';
};

// Pure: derives parent state from its children against current position and max visited
const getParentState = (parent, currentNavValue, maxVisited) => {
  const children = parent.childElements || [];
  if (children.length === 0) {
    return getStepState(parent.value, currentNavValue, maxVisited);
  }
  const currV = Number(currentNavValue).toFixed(1);
  const maxV = Number(maxVisited).toFixed(1);

  const anyChildCurrent = children.some(
    (child) => Number(child.value).toFixed(1) === currV
  );
  if (anyChildCurrent) return 'current';

  const allChildrenVisited = children.every(
    (child) => Number(child.value).toFixed(1) <= maxV
  );
  if (allChildrenVisited) return 'active';

  return 'disabled';
};

/* eslint complexity: ["error", 27] -- need to increase complexity */
const VerticalMultiTracker = ({
  cssProperties,
  setFormSubmissionPayload,
  formSubmissionPayload,
  childElements,
}) => {
  // maxVisitedRef tracks the highest navDefaultSection ever reached.
  // Using a ref (not state) avoids extra re-renders — it is read during
  // render as a derived input, not as a trigger for re-renders.
  const maxVisitedRef = useRef(formSubmissionPayload?.navDefaultSection ?? null);

  const currentNavValue = Number(formSubmissionPayload?.navDefaultSection).toFixed(1);

  useEffect(() => {
    const incoming = formSubmissionPayload?.navDefaultSection;
    if (incoming != null && (maxVisitedRef.current == null || incoming > maxVisitedRef.current)) {
      maxVisitedRef.current = incoming;
    }
  }, [formSubmissionPayload?.navDefaultSection]);

  const handleClick = (navVal) => {
    if (navVal <= maxVisitedRef.current) {
      setFormSubmissionPayload((prev) => ({
        ...prev,
        navDefaultSection: navVal,
        elemId: null,
      }));
    }
  };

  // Flatten parent steps and their childElements into a single ordered list.
  // Each parent becomes a { type: 'parent', ... } entry, each child becomes
  // a { type: 'sub', ... } entry, keeping insertion order so the connector
  // line passes through substep dots between parent dots.
  const flatItems = [];
  if (childElements) {
    childElements.forEach((parent) => {
      flatItems.push({ type: 'parent', ...parent });
      if (parent.childElements && parent.childElements.length > 0) {
        parent.childElements.forEach((sub) => {
          flatItems.push({ type: 'sub', ...sub });
        });
      }
    });
  }

  if (flatItems.length === 0) return null;

  return (
    <div
      className={`vertical-timeline-tracker ${cssProperties?.style || 'col-md-12 margin-3-tb'}`}
      style={cssProperties?.customCss || {}}
    >
      <style>{timelineStyles}</style>
      <nav aria-label="Application Progress">
        <ol className="vt-list">
          {flatItems.map((item, index) => {
            const isLast = index === flatItems.length - 1;
            const isParent = item.type === 'parent';
            const maxVisited = maxVisitedRef.current;

            const state = isParent
              ? getParentState(item, currentNavValue, maxVisited)
              : getStepState(item.value, currentNavValue, maxVisited);
            const icon = isParent && item.icon ? getIcon(item.icon) : null;

            const dotClasses = isParent ? [
              'vt-dot',
              'vt-dot--parent',
              state === 'current' && 'vt-dot--current',
              state === 'active' && 'vt-dot--active',
            ].filter(Boolean).join(' ') : null;

            const subDotClasses = !isParent ? [
              'vt-dot',
              'vt-dot--sub',
              state === 'active' && 'vt-dot--active',
            ].filter(Boolean).join(' ') : null;

            const labelClasses = [
              'vt-label',
              !isParent && 'vt-label--sub',
              state === 'current' && 'vt-label--current',
              state === 'active' && 'vt-label--active',
              state === 'disabled' && 'vt-label--disabled',
            ].filter(Boolean).join(' ');

            const lineClasses = [
              'vt-line',
              isParent ? 'vt-lineHeight--parent' : 'vt-lineHeight--sub',
              (state === 'active' || (isParent && state === 'current')) && 'vt-line--active',
            ].filter(Boolean).join(' ');

            return (
              <li
                key={`${item.type}-${item.id || item.label}`}
                className={`vt-item ${isParent ? 'vt-item--parent' : 'vt-item--sub'}`}
              >
                <div className="vt-connector">
                  {isParent && (
                    <span className={dotClasses} aria-hidden="true">
                      {state === 'active' && <CheckIcon />}
                    </span>
                  )}
                  {!isParent && (
                    <div style={{ paddingLeft: '6px' }} />
                  )}
                  {!isLast && <span className={lineClasses} aria-hidden="true" />}
                </div>
                <div className="vt-content">
                  <button
                    type="button"
                    className={labelClasses}
                    onClick={() => handleClick(item.value)}
                    aria-current={state === 'current' ? 'step' : undefined}
                  >
                    {icon && <span className="vt-icon">{icon}</span>}
                    {!isParent && state === 'active' && (
                    <span className={subDotClasses} aria-hidden="true" style={{ marginRight: '6px', flexShrink: 0 }}>
                      <CheckIcon />
                    </span>
                    )}
                    {item.label}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

VerticalMultiTracker.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.string),
  childElements: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
      icon: PropTypes.string,
      id: PropTypes.string,
      childElements: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.number,
          id: PropTypes.string,
        })
      ),
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types -- formSubmissionPayload shape is dynamic based on form fields
  formSubmissionPayload: PropTypes.objectOf(PropTypes.any),
  setFormSubmissionPayload: PropTypes.func,
};

export default VerticalMultiTracker;
